<script setup lang="ts">
import { fabGoogle, fabMicrosoft } from '@quasar/extras/fontawesome-v6'
import { ref, onMounted } from 'vue'

const authUrl = `${import.meta.env.VITE_API_URL}/auth/google`
const microsoftAuthUrl = `${import.meta.env.VITE_API_URL}/auth/microsoft`
const open = ref(true)
const animateIn = ref(false)

onMounted(() => {
  requestAnimationFrame(() => {
    animateIn.value = true
  })
})
</script>

<template>
  <q-dialog v-model="open" persistent transition-show="fade" transition-hide="fade" role="dialog" aria-label="Login">
    <div class="myCard login-card" :class="{ 'animate-in': animateIn }" role="document">
      <q-img src="@/assets/images/Square_Full_Logo.png" class="q-my-md" loading="eager" alt="MeetingHero Logo" />

      <div class="welcome-text text-center q-pb-md">
        <h1 class="text-h5 text-weight-bold q-mb-xs">Welcome to MeetingHero</h1>
        <p class="text-subtitle1 q-my-xs">Your automated meeting prep awaits</p>
      </div>

      <div class="column q-pa-md q-gutter-y-md">
        <a :href="authUrl" class="full-width text-decoration-none">
          <q-btn
            class="full-width auth-btn google-btn"
            :icon="fabGoogle"
            label="Continue with Google"
            unelevated
            size="md"
          >
            <q-tooltip>Sign in or create a new account with Google</q-tooltip>
          </q-btn>
        </a>

        <a :href="microsoftAuthUrl" class="full-width text-decoration-none">
          <q-btn
            class="full-width auth-btn microsoft-btn"
            :icon="fabMicrosoft"
            label="Continue with Microsoft"
            unelevated
            size="md"
          >
            <q-tooltip>Sign in or create a new account with Microsoft</q-tooltip>
          </q-btn>
        </a>
      </div>
      <div class="text-center q-mb-md text-caption text-grey-7">
        By continuing, you agree to our
        <a
          href="https://meetinghero.ai/termsofservice"
          target="_blank"
          rel="noopener noreferrer"
          class="text-primary text-weight-medium"
          >Terms of Service</a
        >
        and
        <a
          href="https://meetinghero.ai/privacypolicy"
          target="_blank"
          rel="noopener noreferrer"
          class="text-primary text-weight-medium"
          >Privacy Policy</a
        >
      </div>
    </div>
  </q-dialog>
</template>

<style scoped>
.login-card {
  width: 100%;
  max-width: 420px;
  border-radius: 20px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  position: relative;
  opacity: 0;
  transform: translateY(20px);
  transition:
    opacity 0.3s ease-out,
    transform 0.3s ease-out;
  will-change: opacity, transform;
}

.animate-in {
  opacity: 1;
  transform: translateY(0);
}

.welcome-text {
  background: radial-gradient(circle at center, rgba(12, 12, 12, 0.95), rgba(18, 18, 18, 0.85));
  padding: 1rem 0;
  color: white;
}

.auth-btn {
  border-radius: 12px;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
  height: 56px;
  will-change: transform, box-shadow;
}

.google-btn {
  background: #4285f4;
  color: white;
}

.microsoft-btn {
  background: #2f2f2f;
  color: white;
}

.auth-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.auth-btn:active:not(:disabled) {
  transform: translateY(0);
}

.text-decoration-none {
  text-decoration: none;
}

@media (max-width: 599px) {
  .login-card {
    max-width: 90vw;
    margin: 0 auto;
  }
}

@media (prefers-reduced-motion: reduce) {
  .login-card,
  .auth-btn {
    transition: none;
  }

  .auth-btn:hover:not(:disabled) {
    transform: none;
  }
}
</style>
